import styled from "@emotion/styled";
import { Theme } from "@emotion/react";
import { MDSTextProps, MDSTextVariant, MDSTextOverflow, MDSTextColor } from "./text.types";

const getColor = (theme: Theme, color: MDSTextColor): string => {
  switch (color) {
    case MDSTextColor.Primary:
      return theme.colors.text.primary;
    case MDSTextColor.Secondary:
      return theme.colors.text.secondary;
    case MDSTextColor.Tertiary:
      return theme.colors.text.tertiary;
    case MDSTextColor.Contrast:
      return theme.colors.text.contrast;
    case MDSTextColor.OnAccent:
      return theme.colors.text.onAccent;
    case MDSTextColor.Link:
      return theme.colors.text.link.default;
    case MDSTextColor.LinkHover:
      return theme.colors.text.link.hover;
    case MDSTextColor.Emphasis:
      return theme.colors.text.emphasis;
    case MDSTextColor.Positive:
      return theme.colors.text.positive;
    case MDSTextColor.Danger:
      return theme.colors.text.danger;
    default:
      return theme.colors.text.primary;
  }
};

export const StyledText = styled.span<{
  $variant: MDSTextVariant;
  $weight?: MDSTextProps["weight"];
  $color?: MDSTextColor;
  $overflow?: MDSTextOverflow;
  $numberOfLines?: number;
}>`
  margin: 0;
  color: ${({ theme, $color }) => ($color ? getColor(theme, $color) : theme.colors.text.primary)};
  font-size: ${({ theme, $variant }) =>
    (theme.fontSizes as Record<MDSTextVariant, string>)[$variant]};
  line-height: ${({ theme, $variant }) =>
    (theme.lineHeights as Record<MDSTextVariant, string>)[$variant]};
  letter-spacing: ${({ theme, $variant }) =>
    (theme.lineSpacings as Record<MDSTextVariant, string>)[$variant]};
  font-weight: ${({ theme, $variant, $weight }) =>
    $weight
      ? theme.fontWeights[$weight]
      : String($variant).startsWith("header-")
        ? theme.fontWeights.semiBold
        : [MDSTextVariant.TextM, MDSTextVariant.TextSm].includes($variant)
          ? theme.fontWeights.smedium
          : theme.fontWeights.regular};

  ${({ $variant }) => {
    if ($variant === MDSTextVariant.CodeParagraphSm || $variant === MDSTextVariant.CodeTextSm) {
      return `
        font-family: "Ritual Mono", SFMono-Regular, Consolas, 'Liberation Mono', Menlo, monospace;
      `;
    }
    return "";
  }}

  /* Overflow styles */
  ${({ $overflow }) => {
    if (!$overflow || $overflow === MDSTextOverflow.Regular) return "";

    if ($overflow === MDSTextOverflow.Ellipsis) {
      return `
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
      `;
    }

    if ($overflow === MDSTextOverflow.EllipsisWrap) {
      return `
        overflow: hidden;
        white-space: wrap;
        text-overflow: ellipsis;
      `;
    }

    return "";
  }}

  /* Number of lines styles */
  ${({ $numberOfLines }) => {
    if (!$numberOfLines) return "";

    return `
      overflow: hidden;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: ${$numberOfLines};
      max-height: ${$numberOfLines}lh;
    `;
  }}
`;
